// @flow
import { useMemo } from 'react';
import { forEach } from 'ramda';

import SimpleDate from 'app/dataProviders/SimpleDate';
import type { Klass } from 'app/dataTypes/Klasses.types';


type Options = {
  date: SimpleDate | null,
  changeDate: (date: SimpleDate) => void,
  klass: Klass | null,
  dateFormat: string,
}

type Weeks = Map<string, { prev: SimpleDate | null, next: SimpleDate | null }>

export default function useAvailableWeeks({ date, changeDate, klass, dateFormat }: Options): {
  onPrevWeek: null | () => void,
  onNextWeek: null | () => void,
} {
  const currentWeekBeginAt = date ? new SimpleDate(...date.startOfWeek()) : null;

  const weeks: Weeks = useMemo(() => {
    const weeksMap = new Map();
    if (klass && klass.schoolyearPeriods instanceof Array) {
      const periods = klass.schoolyearPeriods;
      let currentWeekStart = null;
      let prevWeekStart = null;

      forEach((period) => {
        if (period.start && period.finish) {
          currentWeekStart = new SimpleDate(...period.start.startOfWeek());

          for (let i = 0; i < 100; i++) {
            if (currentWeekStart.isAfter(period.finish)) {
              break;
            }
            weeksMap.set(currentWeekStart.getString(dateFormat), {
              prev: prevWeekStart,
              next: null,
            });
            if (prevWeekStart) {
              const key = prevWeekStart.getString(dateFormat);
              weeksMap.set(key, {
                ...(weeksMap.get(key) || { prev: null }),
                next: currentWeekStart,
              });
            }
            const nextWeekStart = new SimpleDate(...currentWeekStart.nextWeek());
            prevWeekStart = currentWeekStart;
            currentWeekStart = nextWeekStart;
          }
        }
      }, periods);
    }

    return weeksMap;
  }, [klass, dateFormat]);

  const res = useMemo(() => {
    const weeksTotal = weeks.size;
    const currentDates = currentWeekBeginAt ? weeks.get(currentWeekBeginAt.getString(dateFormat)) : null;
    if (weeksTotal && currentDates) {
      const previosDate = currentDates.prev;
      const nextDate = currentDates.next;

      const onPrevWeek = previosDate ? () => {
        changeDate(previosDate);
      } : null;
      const onNextWeek = nextDate ? () => {
        changeDate(nextDate);
      } : null;

      return { onPrevWeek, onNextWeek };
    }

    return {
      onPrevWeek: null,
      onNextWeek: null,
    };
  }, [weeks, currentWeekBeginAt, dateFormat, changeDate]);

  return res;
}
